/**
 * billingData: the reads and writes behind the Usage page's "Credits and
 * guardrails" section: the same endpoints Settings · Billing calls
 * (`/api/billing/*`, `/api/settings/guardrails`), so the two pages always say
 * the same thing. Each read fails on its own: a failed credits read costs the
 * credit tiles, never the daily ceiling.
 */
import { useCallback, useEffect, useState } from "react";
import type { CreditBalance, CreditTransaction, WorkspaceBillingStatus } from "@yagni/shared";

export interface BillingOverviewData {
  status: WorkspaceBillingStatus;
  credits: CreditBalance;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  signupCreditGranted: boolean;
}

export interface CreditsData {
  credits: CreditBalance;
  transactions: CreditTransaction[];
  total: number;
  /** Complete per-day spend (credits), in `timezone`. */
  dailySpend?: { day: string; credits: number }[];
  timezone?: string;
}

export interface GuardrailsData {
  dailyCreditCeiling: number;
  dailyCreditsUsed: number;
  dailyCostCeilingMillicents: number;
  dailyTokenResetAt: string | null;
}

const NETWORK_ERROR = "Something went wrong reaching billing. Check your connection and try again.";

async function readJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) {
      // The status line only, never the body: enough to tell a 403 from a 500.
      console.warn("[usage] billing.read_failed", url.split("?")[0], res.status);
      return null;
    }
    return (await res.json()) as T;
  } catch {
    console.warn("[usage] billing.read_failed", url.split("?")[0], "network");
    return null;
  }
}

/** The server's `{ error }` on a refused billing call, else the fallback. */
async function errorOf(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error.trim()) return body.error;
  } catch {
    // Not JSON: the fallback says enough.
  }
  return fallback;
}

/** The daily credit ceiling: read, and saved through the same PATCH Billing uses. */
export function useGuardrails() {
  const [guardrails, setGuardrails] = useState<GuardrailsData | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void readJson<GuardrailsData>("/api/settings/guardrails").then((body) => {
      if (cancelled) return;
      setGuardrails(body);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (dailyCreditCeiling: number) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/settings/guardrails", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dailyCreditCeiling }),
      });
      if (res.ok) setGuardrails((await res.json()) as GuardrailsData);
      else setError(await errorOf(res, "Could not update the daily limit. Try again?"));
    } catch {
      setError("Could not update the daily limit. Try again?");
    } finally {
      setSaving(false);
    }
  }, []);

  return { guardrails, loaded, saving, error, save };
}

/** Credits and credit activity. Nothing is for sale: the plan is managed by YAGNI. */
export function useBilling() {
  const [overview, setOverview] = useState<BillingOverviewData | null>(null);
  const [credits, setCredits] = useState<CreditsData | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [nextOverview, nextCredits] = await Promise.all([
        readJson<BillingOverviewData>("/api/billing/overview"),
        readJson<CreditsData>("/api/billing/credits?limit=50"),
      ]);
      if (cancelled) return;
      setOverview(nextOverview);
      setCredits(nextCredits);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Opens Stripe's portal: invoices, receipts and the payment method. */
  const manageBilling = useCallback(async () => {
    setActionError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/billing/portal", { method: "POST", credentials: "include" });
      if (res.ok) {
        const { url } = (await res.json()) as { url?: string };
        if (url) {
          window.location.href = url;
          return;
        }
      }
      setActionError(
        res.ok
          ? "We couldn't open the billing portal. Please try again, or contact support."
          : await errorOf(res, "We couldn't open the billing portal. Please try again, or contact support."),
      );
    } catch {
      setActionError(NETWORK_ERROR);
    } finally {
      setBusy(false);
    }
  }, []);

  const loadMoreActivity = useCallback(async () => {
    if (!credits || moreLoading) return;
    setMoreLoading(true);
    setMoreError(null);
    const more = await readJson<CreditsData>(`/api/billing/credits?limit=50&offset=${credits.transactions.length}`);
    if (more) setCredits({ ...more, transactions: [...credits.transactions, ...more.transactions] });
    else setMoreError("More credit activity could not be loaded. Try again?");
    setMoreLoading(false);
  }, [credits, moreLoading]);

  return {
    overview,
    credits,
    loaded,
    busy,
    moreLoading,
    moreError,
    actionError,
    manageBilling,
    loadMoreActivity,
  };
}

/** A credit amount as Billing prints it: whole from 100, one decimal from 1. */
export function formatCredits(n: number): string {
  if (n >= 100) return Math.round(n).toLocaleString("en-US");
  if (n >= 1) return n.toFixed(1);
  return n.toFixed(2);
}

/** Never print a raw "LLM: <model>" ledger line; mirrors the server's redaction. */
export function creditDescription(tx: CreditTransaction): string {
  const text = tx.description ?? tx.category.replace(/_/g, " ");
  const llm = text.match(/^\s*(Partial\s+)?LLM:.*?(?:\(([\d,]+)\s+tokens?\))?\s*$/i);
  if (llm) {
    const prefix = llm[1] ? "Partial AI usage" : "AI usage";
    return llm[2] ? `${prefix} (${llm[2]} tokens)` : prefix;
  }
  return text;
}

export interface CreditActivityLine {
  key: string;
  day: string;
  label: string;
  /** Signed credits: negative spends, positive adds. */
  amount: number;
}

/**
 * The credit feed by day, newest first: a day's spend collapses to one AI
 * usage line (the server's complete daily total when it sends one), and every
 * addition keeps its own line.
 */
export function creditActivity(data: CreditsData): CreditActivityLine[] {
  const zone = data.timezone || undefined;
  const dayKey = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" });
  const dayLabel = new Intl.DateTimeFormat("en-US", { timeZone: zone, month: "short", day: "numeric" });
  const complete = new Map((data.dailySpend ?? []).map((entry) => [entry.day, entry.credits]));
  const order: string[] = [];
  const days = new Map<string, { label: string; spend: number; adds: CreditTransaction[] }>();
  for (const tx of data.transactions) {
    const when = new Date(tx.createdAt);
    const key = dayKey.format(when);
    let bucket = days.get(key);
    if (!bucket) {
      bucket = { label: dayLabel.format(when), spend: 0, adds: [] };
      days.set(key, bucket);
      order.push(key);
    }
    if (tx.amount < 0) bucket.spend += -tx.amount;
    else if (tx.amount > 0) bucket.adds.push(tx);
  }
  const lines: CreditActivityLine[] = [];
  for (const key of order) {
    const bucket = days.get(key)!;
    const spend = complete.get(key) ?? bucket.spend;
    if (spend > 0) lines.push({ key: `${key}-spend`, day: bucket.label, label: "AI usage", amount: -spend });
    for (const tx of bucket.adds) {
      lines.push({ key: tx.id, day: bucket.label, label: creditDescription(tx), amount: tx.amount });
    }
  }
  return lines;
}
